import React, { useEffect, useState, useMemo } from 'react';
import { Button, Checkbox, Divider, FormControlLabel, styled, Grid, TextField, Typography, Chip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, CircularProgress, Box, Tooltip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { AddEnrollmentsTransformRequest, SchoolListItem, SiteDTO, TransformEnrollmentDefinition, TransformAddEnrollmentDTO } from '@beable/roster-backend-client-v1';
import { Autocomplete } from '@mui/material';
import { uniqueId, sortBy } from 'lodash';
import transformService from '../../services/TransformService';
import { v1 } from 'uuid';
import SuccessDialog from './SuccessDialog';
import Search from '@mui/icons-material/Search';
import { Loading } from '../Common/Layout/Loading';
import { getStyles } from './styles';

const Form = styled('form')(({ theme }) => ({
    width: '100%',
    marginTop: theme.spacing(1),
}));

type Option = {
    value: string;
    label: string;
    schoolId?: string;
};

type ClassOption = {
    value: string;
    label: string;
    schoolName: string;
    schoolExternalId: string;
};

interface AdhocEnrollmentData {
    classes: ClassOption[];
    schools: Option[];
    user: Option;
    role: 'teacher' | 'student' | '';
    primary?: string;
}

const defaultState: AdhocEnrollmentData = {
    classes: [],
    schools: [],
    user: { value: '', label: '' },
    role: '',
    primary: ''
};

const allClassesOption = [{ value: 'select-all', label: 'All Classes Selected', schoolExternalId: '', schoolName: '' }]
const roleOptions: Option[] = [{ value: 'teacher', label: 'Teacher' }, { value: 'student', label: 'Student' }];
const primaryOptions: Option[] = [{ value: 'true', label: 'True' }, { value: 'false', label: 'False' }];

const searchFieldOptions: Option[] = [
    { value: 'all', label: 'All Fields' },
    { value: 'siteId', label: 'siteId' },
    { value: 'sourcedId', label: 'sourcedId' },
    { value: 'classSourcedId', label: 'classSourcedId' },
    { value: 'schoolSourcedId', label: 'schoolSourcedId' },
    { value: 'userSourcedId', label: 'userSourcedId' },
    { value: 'role', label: 'role' },
    { value: 'orgName', label: 'Org.name' },
    { value: 'className', label: 'Class.name' },
    { value: 'courseName', label: 'Course.name' },
    { value: 'userName', label: 'User.userName' },
    { value: 'userFirstName', label: 'User.firstName' },
    { value: 'userLastName', label: 'User.lastName' }
];

interface EnrollmentsTransformProps {
    site: SiteDTO | undefined;
    isSiteSelected: boolean;
}

export default function EnrollmentsTransform({ site, isSiteSelected }: EnrollmentsTransformProps) {
    const theme = useTheme();
    const styles = getStyles(theme);

    const [adHocEnrollment, setAdhocEnrollment] = useState<AdhocEnrollmentData>(defaultState);
    const { classes, schools, user, role, primary } = adHocEnrollment;

    const [isSchoolSelected, setIsSchoolSelected] = useState<boolean>(false);
    const [selectAllClasses, setSelectAllClasses] = useState<boolean>(false);

    const [userOptions, setUserOptions] = useState<Option[]>([]);
    const [classOptions, setClassOptions] = useState<ClassOption[]>([]);
    const [schoolOptions, setSchoolOptions] = useState<Option[]>([]);
    const [schoolsList, setSchoolsList] = useState<SchoolListItem[]>([]);
    const [existingRecords, setExistingRecords] = useState<TransformAddEnrollmentDTO[]>([]);
    const [loadingRecords, setLoadingRecords] = useState(false);

    const [formSubmitting, setFormSubmitting] = useState(false);
    const [forceReset, setForceReset] = useState('');
    const [dialog, showDialog] = useState(false);
    const [successSaving, setSuccessSaving] = useState(false);
    const [expandedRecord, setExpandedRecord] = useState<string | null>(null);
    const [searchCriteria, setSearchCriteria] = useState<Array<{field: string, value: string}>>([]);

    const [searchTerm, setSearchTerm] = useState('');
    const [isSearchLoading, setIsSearchLoading] = useState(false);
    const [hasSearched, setHasSearched] = useState(false);
    const [midSearch, setMidSearch] = useState(false);
    const [searchedTerm, setSearchedTerm] = useState('');

    const setDataForLists = async (siteId: number) => {
        await Promise.all([
            getSchools(siteId),
            fetchExistingRecords(siteId),
        ]);
        if (isSchoolSelected) await getClasses(siteId, schools);
    };

    useEffect(() => {
        handleResetForm();
        if (site?.id) {
            setDataForLists(site.id);
        }
    }, [site]);

    const getSchools = async (siteId: number) => {
        const data = await transformService.getSchools(siteId);
        if (!data) {
            setSchoolsList([]);
            setSchoolOptions([])
            return;
        }
        setSchoolsList(data);
        setSchoolOptions(data.map((school) => ({
            value: school.externalSourceId,
            label: school.name
        })));
    };

    const getClasses = async (siteId: number, selectedSchools: Option[]) => {
        const filteredSchools = schoolsList.filter(s => {
            return selectedSchools.find(selectedSchool => {
                return selectedSchool.value === s.externalSourceId;
            });
        });
        const orgIds = filteredSchools.map(s => s.id);

        const resp = await transformService.findActiveClassesbyOrgIds(siteId, orgIds);
        if (!resp) {
            setClassOptions([]);
            return;
        }
        const data = resp?.classes;
        const classesToSet: ClassOption[] = data.map(c => {
            const org = schoolsList.find(s => s.id === c.orgId);
            return {
                value: c.externalSourceId,
                label: c.name,
                schoolName: org?.name ?? '',
                schoolExternalId: org?.externalSourceId ?? ''
            };
        });
        setClassOptions(classesToSet);
        if (selectAllClasses) {
            setAdhocEnrollment({
                ...adHocEnrollment,
                classes: classesToSet
            });
        }
    };

    const fetchExistingRecords = async (siteId: number) => {
        setLoadingRecords(true);
        const data = await transformService.getTransformAddEnrollments(siteId);

        if (data) {
            setExistingRecords(data);
        } else {
            setExistingRecords([]);
        }
        setLoadingRecords(false);
    };

    const handleInputChange = (event: any) => {
        event.persist();
        setSearchTerm(event.target.value);
        if (hasSearched) {
            setMidSearch(true);
        }
        setHasSearched(false);
    };
    const handleKeyPress = (event: any) => {
        if (event.key === "Enter") {
            event.preventDefault();
            handleUserSearch();
        }
    };
    const handleUserSearch = async () => {
        setIsSearchLoading(true);

        const result = await transformService.getUsersBySubstring(
            site!.id,
            searchTerm
        );
        const userOptions = !result ? [] : result.map((user) => ({
            value: user.externalSourceId ?? '',
            label: user.username
        }));
        setUserOptions(userOptions);
        setSearchedTerm(searchTerm);
        setHasSearched(true);
        setIsSearchLoading(false);
        setMidSearch(false);
    };


    const handleSchoolChange = (value: any) => {
        setIsSchoolSelected(!!value.length);
        if (site?.id && value.length) {
            getClasses(site.id, value);
        }
        if (!value.length) {
            setAdhocEnrollment({
                ...adHocEnrollment,
                classes: []
            });
            setClassOptions([]);
            setSelectAllClasses(false);
        }
    };

    const handleClassCheck = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSelectAllClasses(event.target.checked);
        setAdhocEnrollment({
            ...adHocEnrollment,
            classes: event.target.checked ? classOptions : []
        });
    };

    const handleSelectChange =
        (prop: keyof AdhocEnrollmentData, value: any) => {
            setAdhocEnrollment({
                ...adHocEnrollment,
                [prop]: value,
            });
            if (prop === 'schools' && value) { handleSchoolChange(value) }
        };


    const handleResetForm = () => {
        setAdhocEnrollment(defaultState);
        setIsSchoolSelected(false);
        setSelectAllClasses(false);
        setClassOptions([]);
        setForceReset(uniqueId());
        setUserOptions([]);
        setSearchTerm('');
        setIsSearchLoading(false);
        setHasSearched(false);
        setSearchedTerm('');
    };

    const isFormValid = () => {
        if (!adHocEnrollment.schools?.length) {
            return false;
        }
        if (!adHocEnrollment.classes?.length) {
            return false;
        }
        if (!adHocEnrollment.user || adHocEnrollment.user?.value === '') {
            return false;
        }
        if (adHocEnrollment.role === '' || !adHocEnrollment.role) {
            return false;
        }
        return true;
    };

    const handleSaveForm = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        setFormSubmitting(true);

        let enrollments: TransformEnrollmentDefinition[];
        if (role !== '') {
            enrollments = classes.map(c => {
                return {
                    sourcedId: v1(), //generated uuid
                    classSourcedId: c.value,
                    schoolSourcedId: c.schoolExternalId,
                    userSourcedId: user.value,
                    role,
                    primary: primary === 'true' ? true : false,
                };
            });
        }
        else {
            return;
        }
        const req: AddEnrollmentsTransformRequest = {
            siteId: site?.id ?? 0,
            enrollments
        };

        const response = await transformService.saveAddEnrollmentsTransformation(req);

        setFormSubmitting(false);
        setSuccessSaving(true);

        if (!response) {
            setSuccessSaving(false);
        }
        showDialog(true);
    };

    const closeDialog = () => {
        setSuccessSaving(false);
        showDialog(false);
        handleResetForm();
        if (site?.id) {
            fetchExistingRecords(site.id);
        }
    };

    const handleViewRecord = (record: TransformAddEnrollmentDTO) => {
        if (expandedRecord === record.id) {
            // If already expanded, hide it
            setExpandedRecord(null);
        } else {
            // If not expanded, show it
            setExpandedRecord(record.id || null);
        }
    };

    const getFieldValue = (record: TransformAddEnrollmentDTO, field: string): string => {
        switch (field) {
            case 'siteId':
                return record.siteId?.toString() || '';
            case 'sourcedId':
                return record.sourcedId || '';
            case 'classSourcedId':
                return record.classSourcedId || '';
            case 'schoolSourcedId':
                return record.schoolSourcedId || '';
            case 'userSourcedId':
                return record.userSourcedId || '';
            case 'role':
                return record.role || '';
            case 'orgName':
                return record.org?.name || '';
            case 'className':
                return record.class?.name || '';
            case 'courseName':
                return record.course?.name || '';
            case 'userName':
                return record.user?.userName || '';
            case 'userFirstName':
                return record.user?.firstName || '';
            case 'userLastName':
                return record.user?.lastName || '';
            default:
                return '';
        }
    };

    const filteredRecords = useMemo(() => {
        return existingRecords.filter(record => {
            if (searchCriteria.length === 0) {
                return true;
            }

            const matchesAllCriteria = searchCriteria.every(criteria => {
                const fieldValue = getFieldValue(record, criteria.field).toLowerCase();
                const searchValue = criteria.value.toLowerCase();
                return fieldValue.includes(searchValue);
            });
            
            return matchesAllCriteria;
        });
    }, [existingRecords, searchCriteria]);

    return (
        <>
            {dialog === true && <SuccessDialog closeDialog={closeDialog} success={successSaving} />}
            <Grid size={12}>
                <Grid container>
                    <Grid
                        sx={{ display: "flex", justifyContent: "flex-start", marginTop: '6px' }}
                        size={12}>
                        <Typography variant='h5' align-left='true'>
                            Add transform enrollments for the selected site.
                        </Typography>
                    </Grid>
                    <Grid sx={{ display: "flex", justifyContent: "flex-start" }} size={12}>

                        <p align-left='true'>
                            Fill out and save the following form. This will add records to transformAddEnrollments. <br />
                            To apply these transformations to the site, roster a batch. Once completed, the enrollments will be added to the roster data.
                        </p>
                    </Grid>
                </Grid>
                <Form onSubmit={handleSaveForm}>
                    <Grid sx={styles.formContainer}>
                        <Grid sx={styles.formField}>
                            <TextField
                                variant='outlined'
                                label='Search for user'
                                name='search'
                                data-testid='search'
                                id='search'
                                color='primary'
                                helperText='Search by first name, last name or username'
                                value={searchTerm}
                                disabled={!isSiteSelected || isSearchLoading}
                                slotProps={{
                                    input: {
                                        endAdornment: <Button
                                            endIcon={isSearchLoading ? <Loading size={20} height={'20px'} /> : <Search />}
                                            variant="contained"
                                            aria-label="search-button"
                                            color="primary"
                                            disabled={isSearchLoading || !site || !searchTerm}
                                            onClick={() => handleUserSearch()}
                                        >
                                            Search
                                        </Button>
                                    }
                                }}
                                onChange={handleInputChange}
                                onKeyPress={handleKeyPress}
                            />
                        </Grid>
                        <Grid sx={styles.formField}>
                            <Divider>
                                {!userOptions.length &&
                                    ((midSearch && !hasSearched) || hasSearched) ?
                                    <Chip
                                        sx={styles.chip}
                                        label={<>No users found with name {<><Typography component="span" sx={{ fontWeight: "bold" }}>{searchedTerm}</Typography></>}</>}
                                    /> : undefined}
                            </Divider>
                        </Grid>
                        <Grid sx={styles.formField}>
                            <Autocomplete
                                key={forceReset}
                                multiple={false}
                                filterSelectedOptions={false}
                                isOptionEqualToValue={(option, value) => option.value === value.value}
                                id={'user'}
                                aria-label='user-dropdown'
                                options={userOptions}
                                getOptionLabel={(option) => option.label}
                                disabled={!isSiteSelected || !userOptions.length}
                                onChange={(_event, value) => handleSelectChange('user', value)}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        variant='outlined'
                                        label={!userOptions.length ? 'Search for valid user' : 'User'}
                                        required={true}
                                        slotProps={{
                                            ...params.slotProps,

                                            htmlInput: {
                                                ...params.slotProps.htmlInput,
                                                required: user?.value === ''
                                            }
                                        }}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid sx={styles.formField}>
                            <Autocomplete
                                key={forceReset}
                                multiple={true}
                                filterSelectedOptions={true}
                                isOptionEqualToValue={(option, value) => option.value === value.value}
                                id={'schools'}
                                options={schoolOptions}
                                data-testid='schools'
                                aria-label='schools-dropdown'
                                getOptionLabel={(option) => option.label}
                                disabled={!isSiteSelected}
                                onChange={(_event, value) => handleSelectChange('schools', value)}
                                disableCloseOnSelect
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        variant='outlined'
                                        label='Schools'
                                        required={true}
                                        slotProps={{
                                            ...params.slotProps,

                                            htmlInput: {
                                                ...params.slotProps.htmlInput,
                                                required: schools?.length === 0,
                                            }
                                        }}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid sx={styles.formField}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        color='primary'
                                        disabled={!isSiteSelected || !isSchoolSelected}
                                        onChange={handleClassCheck}
                                        aria-label='class-checkbox'
                                        checked={selectAllClasses}
                                    />
                                }
                                label='Enroll user in all classes of selected schools'
                            />
                            <Autocomplete
                                key={forceReset}
                                multiple={true}
                                filterSelectedOptions={true}
                                isOptionEqualToValue={(option, value) => option.value === value.value}
                                id={'classes'}
                                aria-label='classes-dropdown'
                                options={selectAllClasses ? allClassesOption : sortBy(classOptions, 'schoolName')}
                                groupBy={(option) => option.schoolName}
                                value={selectAllClasses ? allClassesOption : classes}
                                getOptionLabel={(option) => option.label}
                                disabled={!isSiteSelected || selectAllClasses || !isSchoolSelected}
                                onChange={(_event, value) => handleSelectChange('classes', value)}
                                disableCloseOnSelect
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        variant='outlined'
                                        label={isSchoolSelected ? 'Classes' : 'Select schools to view classes'}
                                        required={true}
                                        slotProps={{
                                            ...params.slotProps,

                                            htmlInput: {
                                                ...params.slotProps.htmlInput,
                                                required: classes?.length === 0
                                            }
                                        }}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid sx={styles.formField}>
                            <Autocomplete
                                key={forceReset}
                                multiple={false}
                                filterSelectedOptions={false}
                                isOptionEqualToValue={(option, value) => option.value === value.value}
                                id={'role'}
                                aria-label='role-dropdown'
                                options={roleOptions}
                                getOptionLabel={(option) => option.label}
                                disabled={!isSiteSelected}
                                onChange={(_event, value) => handleSelectChange('role', value?.value)}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        variant='outlined'
                                        label='Role'
                                        required={true}
                                        slotProps={{
                                            ...params.slotProps,

                                            htmlInput: {
                                                ...params.slotProps.htmlInput,
                                                required: role === ''
                                            }
                                        }}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid sx={styles.formField}>
                            <Autocomplete
                                key={forceReset}
                                multiple={false}
                                filterSelectedOptions={false}
                                isOptionEqualToValue={(option, value) => option.value === value.value}
                                id={'primary'}
                                aria-label='primary-dropdown'
                                options={primaryOptions}
                                getOptionLabel={(option) => option.label}
                                disabled={!isSiteSelected}
                                onChange={(_event, value) => handleSelectChange('primary', value?.value)}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        variant='outlined'
                                        label='Primary'
                                        required={false}
                                        slotProps={{
                                            ...params.slotProps,

                                            htmlInput: {
                                                ...params.slotProps.htmlInput,
                                                required: false
                                            }
                                        }}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid sx={styles.formButtons} >
                            <Button onClick={handleResetForm} sx={styles.saveForm}>
                                Cancel
                            </Button>
                            <Button
                                color='primary'
                                sx={styles.saveForm}
                                type='submit'
                                disabled={!isFormValid() || formSubmitting}
                                variant='contained'
                                aria-label='save-button'
                            >
                                Save
                            </Button>
                        </Grid>
                    </Grid>
                </Form>
            </Grid>
            {/* Existing Records Section */}
            <Grid sx={{ marginTop: '32px' }} size={12}>
                <Typography variant="h6" sx={{ marginBottom: '16px' }}>
                    Existing Transform Enrollment Records ({filteredRecords.length})
                </Typography>
                
                {/* Advanced Search Controls */}
                <Grid container spacing={2} sx={{ marginBottom: '16px' }}>
                    <Grid size={12}>
                        <Typography variant="subtitle2" color="primary" sx={{ mb: 2 }}>
                            Advanced Search Criteria
                        </Typography>
                        {searchCriteria.map((criteria, index) => (
                            <Grid container spacing={2} key={index} sx={{ mb: 2 }}>
                                <Grid
                                    size={{
                                        xs: 12,
                                        sm: 4
                                    }}>
                                    <Autocomplete
                                        fullWidth
                                        options={searchFieldOptions.filter(opt => opt.value !== 'all')}
                                        value={searchFieldOptions.find(option => option.value === criteria.field) || null}
                                        onChange={(_event, value) => {
                                            const newCriteria = [...searchCriteria];
                                            newCriteria[index].field = value?.value || '';
                                            setSearchCriteria(newCriteria);
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                variant="outlined"
                                                label="Field"
                                                size="small"
                                            />
                                        )}
                                    />
                                </Grid>
                                <Grid
                                    size={{
                                        xs: 12,
                                        sm: 6
                                    }}>
                                    <TextField
                                        fullWidth
                                        variant="outlined"
                                        label="Value"
                                        placeholder="Enter search value"
                                        value={criteria.value}
                                        onChange={(e) => {
                                            const newCriteria = [...searchCriteria];
                                            newCriteria[index].value = e.target.value;
                                            setSearchCriteria(newCriteria);
                                        }}
                                        size="small"
                                    />
                                </Grid>
                                <Grid
                                    size={{
                                        xs: 12,
                                        sm: 2
                                    }}>
                                    <Button
                                        variant="outlined"
                                        color="error"
                                        size="small"
                                        onClick={() => {
                                            const newCriteria = searchCriteria.filter((_, i) => i !== index);
                                            setSearchCriteria(newCriteria);
                                        }}
                                        sx={{ height: '40px' }}
                                    >
                                        Remove
                                    </Button>
                                </Grid>
                            </Grid>
                        ))}
                        <Grid container spacing={2}>
                            <Grid
                                size={{
                                    xs: 12,
                                    sm: 6
                                }}>
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    size="small"
                                    onClick={() => setSearchCriteria([...searchCriteria, { field: '', value: '' }])}
                                    startIcon={<span>+</span>}
                                >
                                    Add Search Criteria
                                </Button>
                            </Grid>
                            <Grid
                                size={{
                                    xs: 12,
                                    sm: 6
                                }}>
                                <Button
                                    variant="text"
                                    color="secondary"
                                    size="small"
                                    onClick={() => setSearchCriteria([])}
                                    disabled={searchCriteria.length === 0}
                                >
                                    Clear All
                                </Button>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
                {loadingRecords ? (
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "center",
                            p: 3
                        }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <TableContainer component={Paper}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Sourced ID</TableCell>
                                    <TableCell>User Sourced ID</TableCell>
                                    <TableCell>Role</TableCell>
                                    <TableCell>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredRecords.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={4} align="center">
                                            {existingRecords.length === 0 
                                                ? 'No existing transform enrollment records found'
                                                : 'No records match the current search criteria'
                                            }
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    filteredRecords.map((record, index) => (
                                        <React.Fragment key={record.id || index}>
                                            <TableRow>
                                                <TableCell sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{record.sourcedId}</TableCell>
                                                <TableCell>{record.userSourcedId}</TableCell>
                                                <TableCell>
                                                    <Chip 
                                                        label={record.role} 
                                                        color={record.role === 'student' ? 'primary' : record.role === 'teacher' ? 'secondary' : 'default'}
                                                        size="small"
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Tooltip title={expandedRecord === record.id ? "Hide Details" : "View Details"}>
                                                        <Button 
                                                            variant="text"
                                                            size="small" 
                                                            color="primary"
                                                            onClick={() => handleViewRecord(record)}
                                                            sx={{ whiteSpace: 'nowrap' }}
                                                        >
                                                            See Details
                                                        </Button>
                                                    </Tooltip>
                                                </TableCell>
                                            </TableRow>
                                            {expandedRecord === record.id && (
                                                <TableRow>
                                                    <TableCell colSpan={4}>
                                                        <Box sx={{ p: 2, bgcolor: 'grey.50' }}>
                                                            <Grid container spacing={2}>
                                                                <Grid
                                                                    size={{
                                                                        xs: 12,
                                                                        sm: 6
                                                                    }}>
                                                                    <Typography variant="subtitle2" color="primary" sx={{ mb: 1, mt: 2 }}>
                                                                        <strong>TransformAddEnrollment</strong>
                                                                    </Typography>
                                                                    <Typography variant="body2" color="textSecondary" sx={{ mb: 1, ml: 2 }}>
                                                                        <strong>ID:</strong> {record.id}
                                                                    </Typography>
                                                                    <Typography variant="body2" color="textSecondary" sx={{ mb: 1, ml: 2 }}>
                                                                        <strong>Site ID:</strong> {record.siteId}
                                                                    </Typography>
                                                                    <Typography variant="body2" color="textSecondary" sx={{ mb: 1, ml: 2 }}>
                                                                        <strong>Sourced ID:</strong> {record.sourcedId}
                                                                    </Typography>
                                                                    <Typography variant="body2" color="textSecondary" sx={{ mb: 1, ml: 2 }}>
                                                                        <strong>Class Sourced ID:</strong> {record.classSourcedId}
                                                                    </Typography>
                                                                    <Typography variant="body2" color="textSecondary" sx={{ mb: 1, ml: 2 }}>
                                                                        <strong>School Sourced ID:</strong> {record.schoolSourcedId}
                                                                    </Typography>
                                                                    <Typography variant="body2" color="textSecondary" sx={{ mb: 1, ml: 2 }}>
                                                                        <strong>User Sourced ID:</strong> {record.userSourcedId}
                                                                    </Typography>
                                                                    <Typography variant="body2" color="textSecondary" sx={{ mb: 1, ml: 2 }}>
                                                                        <strong>Role:</strong> {record.role}
                                                                    </Typography>
                                                                    <Typography variant="body2" color="textSecondary" sx={{ mb: 1, ml: 2 }}>
                                                                        <strong>Primary:</strong> {record.primary || ''}
                                                                    </Typography>
                                                                    <Typography variant="body2" color="textSecondary" sx={{ mb: 1, ml: 2 }}>
                                                                        <strong>Status:</strong> {record.status || ''}
                                                                    </Typography>
                                                                    <Typography variant="body2" color="textSecondary" sx={{ mb: 1, ml: 2 }}>
                                                                        <strong>Begin Date:</strong> {record.beginDate || ''}
                                                                    </Typography>
                                                                    <Typography variant="body2" color="textSecondary" sx={{ mb: 1, ml: 2 }}>
                                                                        <strong>End Date:</strong> {record.endDate || ''}
                                                                    </Typography>
                                                                </Grid>
                                                                <Grid
                                                                    size={{
                                                                        xs: 12,
                                                                        sm: 6
                                                                    }}>
                                                                    {/* Organization Details */}
                                                                    {record.org && (
                                                                        <>
                                                                            <Typography variant="subtitle2" color="primary" sx={{ mb: 1, mt: 2 }}>
                                                                                <strong>Org</strong>
                                                                            </Typography>
                                                                            <Typography variant="body2" color="textSecondary" sx={{ mb: 1, ml: 2 }}>
                                                                                <strong>ID:</strong> {record.org.id}
                                                                            </Typography>
                                                                            <Typography variant="body2" color="textSecondary" sx={{ mb: 1, ml: 2 }}>
                                                                                <strong>Name:</strong> {record.org.name}
                                                                            </Typography>
                                                                            <Typography variant="body2" color="textSecondary" sx={{ mb: 1, ml: 2 }}>
                                                                                <strong>Type:</strong> {record.org.orgType}
                                                                            </Typography>
                                                                        </>
                                                                    )}
                                                                    
                                                                    {/* Class Details */}
                                                                    {record.class && (
                                                                        <>
                                                                            <Typography variant="subtitle2" color="primary" sx={{ mb: 1, mt: 2 }}>
                                                                                <strong>Class</strong>
                                                                            </Typography>
                                                                            <Typography variant="body2" color="textSecondary" sx={{ mb: 1, ml: 2 }}>
                                                                                <strong>ID:</strong> {record.class.id}
                                                                            </Typography>
                                                                            <Typography variant="body2" color="textSecondary" sx={{ mb: 1, ml: 2 }}>
                                                                                <strong>Name:</strong> {record.class.name}
                                                                            </Typography>
                                                                        </>
                                                                    )}
                                                                    
                                                                    {/* Course Details */}
                                                                    {record.course && (
                                                                        <>
                                                                            <Typography variant="subtitle2" color="primary" sx={{ mb: 1, mt: 2 }}>
                                                                                <strong>Course</strong>
                                                                            </Typography>
                                                                            <Typography variant="body2" color="textSecondary" sx={{ mb: 1, ml: 2 }}>
                                                                                <strong>ID:</strong> {record.course.id}
                                                                            </Typography>
                                                                            <Typography variant="body2" color="textSecondary" sx={{ mb: 1, ml: 2 }}>
                                                                                <strong>External Source ID:</strong> {record.course.externalSourceId}
                                                                            </Typography>
                                                                            <Typography variant="body2" color="textSecondary" sx={{ mb: 1, ml: 2 }}>
                                                                                <strong>Name:</strong> {record.course.name}
                                                                            </Typography>
                                                                        </>
                                                                    )}
                                                                    
                                                                    {/* User Details */}
                                                                    {record.user && (
                                                                        <>
                                                                            <Typography variant="subtitle2" color="primary" sx={{ mb: 1, mt: 2 }}>
                                                                                <strong>User</strong>
                                                                            </Typography>
                                                                            <Typography variant="body2" color="textSecondary" sx={{ mb: 1, ml: 2 }}>
                                                                                <strong>ID:</strong> {record.user.id}
                                                                            </Typography>
                                                                            <Typography variant="body2" color="textSecondary" sx={{ mb: 1, ml: 2 }}>
                                                                                <strong>Username:</strong> {record.user.userName}
                                                                            </Typography>
                                                                            <Typography variant="body2" color="textSecondary" sx={{ mb: 1, ml: 2 }}>
                                                                                <strong>First Name:</strong> {record.user.firstName}
                                                                            </Typography>
                                                                            <Typography variant="body2" color="textSecondary" sx={{ mb: 1, ml: 2 }}>
                                                                                <strong>Last Name:</strong> {record.user.lastName}
                                                                            </Typography>
                                                                            <Typography variant="body2" color="textSecondary" sx={{ mb: 1, ml: 2 }}>
                                                                                <strong>Middle Name:</strong> {record.user.middleName || ''}
                                                                            </Typography>
                                                                        </>
                                                                    )}
                                                                </Grid>
                                                            </Grid>
                                                        </Box>
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </React.Fragment>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Grid>
        </>
    );

}
